import last from 'lodash/last';

import { IMoment, moment } from '../moment';
import { fullName } from '../utils';
import { mapSdsEventType } from '@lib/compliance-utils';
import { mapRestrictionReason, RESTRICTION_REASON } from './assign-restriction-reason';
import { DrivingPeriod, IDpLocation, IServerDrivingPeriod, IServerDrivingPeriodNav } from './driving-period';
import { ComplianceFeatureFlagService } from '@services/feature-flags/compliance';

import { ILogVehicle, IServerDispatcher } from '@app/dashboard/logs/log';
import { ILogDriver } from '@app/dashboard/logs/log-view-model';
import { IServerNavigation } from '@misc/detail-view-navigation/server-navigation.interface';
import { ICarouselImageData } from '@misc/carousel/carousel.component';

import { DRIVER_SUGGESTED_BY_MAPPING, ISuggestedDriver } from '@services/trips-list';
import { VEHICLE_REGULATION_MODES } from '@lib/vehicle-regulation-modes';
import { ELD_MODES } from '../eld-modes';

export interface IDrivingPeriod {
  id: number;
  vehicle: ILogVehicle;
  dispatcher?: {
    fullName: string;
    id: number;
  };
  dispatcherName: string;
  driver: ILogDriver;
  startTime: IMoment | string;
  startTimeRaw: string;
  trigger?: TRIP_TRIGGER_SOURCE;
  endTime: IMoment;
  endTimeRaw: string;
  duration: number;
  origin: string;
  destination: string;
  startKilometers: number;
  endKilometers: number;
  distance: number;
  metricUnits: boolean;
  type: string;
  sdsType: string;
  status: string;
  changedAttributes: IChangedAttribute;
  note: string;
  startLocation: string;
  endLocation: string;
  logSuggestionId: number;
  startEventOfflineId: string;
  isInterrupted: boolean;
  isInProgress: boolean;
  locations: IDpLocation[];
  lastLocation: IDpLocation;
  firstLogId: number;
  receivingLog: {
    id: number;
    date: IMoment;
    driver: ILogDriver;
  };
  isAssignable: boolean;
  isEditable: boolean;
  restrictionReason: RESTRICTION_REASON;
  showReassignButton: boolean;
  navigation: IServerNavigation;
  isClaimed: boolean;
  isUnregulated: boolean;
  suggestedDriver: ISuggestedDriver;
  unassignable: boolean;
  driverFacingImages: ICarouselImageData[];
  hideDriverSuggestion: boolean;
  prevTrip: ITripInfo;
  nextTrip: ITripInfo;
  drivingPeriodNav: IServerDrivingPeriodNav;
}

interface ITripInfo {
  driver: {
    id: number;
    name: string;
    isAssignable: boolean;
  };
  endTime?: string;
  startTime?: string;
}

export interface IChangedAttribute {
  startAddress?: string;
  endAddress?: string;
  startDescription?: string;
  endDescription?: string;
  notes?: string;
  notesAuthor?: string;
  notesAuthorId?: number;
  special_duty_status?: string;
}

export interface IServerChangedAttribute {
  start_address: string;
  start_description: string;
  end_address: string;
  end_description: string;
  notes: string;
  notes_added_by: IServerDispatcher;
  special_duty_status: string;
}

export const AI_SUGGESTION_PARAMS = {
  is_driver_mobile_location: $localize`Mobile Location Data Match`,
  is_inspection_driver: $localize`Inspection Reports`,
  is_next_driver: $localize`Next Driver`,
  is_previous_driver: $localize`Previous Driver`,
  is_facial_recognition: $localize`Facial Recognition`,
};

export enum TRIP_TRIGGER_SOURCE {
  GPS = 'gps',
  TELEMATICS = 'telematics',
}

export class DrivingPeriodViewModel {
  private drivingPeriod: DrivingPeriod;
  private data: IServerDrivingPeriod;

  constructor(drivingPeriod: DrivingPeriod, private complianceFeatureFlagService: ComplianceFeatureFlagService) {
    this.drivingPeriod = drivingPeriod;
    this.data = drivingPeriod.data;
  }

  public get(): IDrivingPeriod {
    const data = this.data;
    const drivingPeriod = this.drivingPeriod;
    const vehicle = data.vehicle;
    const driver: any = data.driver || {};
    const status = data.status;
    const locations = this.getLocations();
    const receivingLog = data.receiving_log;
    const logIds = drivingPeriod.logIds;
    const changedAttributes = data.changed_attributes;

    let aiSuggestionParams: string[] = null;
    if (data.suggested_driver && data.suggested_driver.suggested_by) {
      const suggestedBy = data.suggested_driver.suggested_by;

      if (DRIVER_SUGGESTED_BY_MAPPING[suggestedBy] === DRIVER_SUGGESTED_BY_MAPPING.driver_id ||
        DRIVER_SUGGESTED_BY_MAPPING[suggestedBy] === DRIVER_SUGGESTED_BY_MAPPING.face_match
      ) {
        aiSuggestionParams = [AI_SUGGESTION_PARAMS.is_facial_recognition];
      } else if (DRIVER_SUGGESTED_BY_MAPPING[suggestedBy] === DRIVER_SUGGESTED_BY_MAPPING.smart_trip_matching) {
        const aiSuggestionKeys = Object.keys(AI_SUGGESTION_PARAMS).filter((key: string): boolean => data.suggested_driver[key]);
        if (aiSuggestionKeys.length) {
          aiSuggestionParams = aiSuggestionKeys.map(key => AI_SUGGESTION_PARAMS[key]);
        }
      }
    }
    const isPreviousDriverAssignable: boolean = this.getDriverAssignableStatus(data.prev_period);
    const isNextDriverAssignable: boolean = this.getDriverAssignableStatus(data.next_period);
    const isSuggestedDriverAssignable: boolean = this.getDriverAssignableStatus(data.suggested_driver);
    const driverFacingImages: ICarouselImageData[] = data.driver_facing_images
      ? data.driver_facing_images.map(image => ({
        url: image.driver_facing_img_url,
        title: image.img_creation_timestamp,
      }))
      : [];

    if (data?.suggested_driver?.bounded_image_url) {
      driverFacingImages.unshift({
        title: data.suggested_driver.image_datetime,
        url: data?.suggested_driver?.bounded_image_url,
      });
    }
    return {
      id: data.id,
      vehicle: vehicle ? {
        id: vehicle.id,
        number: vehicle.number,
        groupIds: vehicle.group_ids,
        model: vehicle.model,
        make: vehicle.make,
        status: vehicle.status,
      } : null,
      dispatcher: data.dispatcher ? {
        id: data.dispatcher.id,
        fullName: fullName(data.dispatcher),
      } : null,
      dispatcherName: data.dispatcher ? fullName(data.dispatcher) : null,
      driver: {
        id: driver.id,
        driverCompanyId: driver.driver_company_id,
        name: fullName(driver),
        firstName: driver.first_name,
        groupIds: driver.group_ids,
        eldMode: driver.eld_mode,
      },
      trigger: data.trigger,
      startTime: drivingPeriod.startTime,
      endTime: drivingPeriod.endTime,
      endTimeRaw: data.end_time,
      startTimeRaw: data.start_time,
      startLocation: drivingPeriod.startLocation,
      endLocation: drivingPeriod.endLocation,
      duration: drivingPeriod.duration,
      origin: data.start_address || data.start_description,
      destination: data.end_address || data.end_description,
      startKilometers: drivingPeriod.startKilometers,
      endKilometers: drivingPeriod.endKilometers,
      distance: data.distance,
      metricUnits: data.vehicle?.metric_units,
      type: data.type,
      sdsType: data.type ? mapSdsEventType(data.type) : '',
      status,
      changedAttributes: this.changedAttributes(changedAttributes),
      note: data.notes,
      logSuggestionId: data.log_suggestion_id,
      startEventOfflineId: data.start_event_offline_id,
      isInterrupted: status === 'interrupted',
      isInProgress: status === 'in_progress',
      locations,
      lastLocation: last(locations),
      firstLogId: logIds && logIds.length ? logIds[0] : null,
      receivingLog: !receivingLog ? null : {
        id: receivingLog.id,
        date: moment(receivingLog.date, 'YYYY-MM-DD'),
        driver: {
          id: receivingLog.driver?.id,
          name: fullName(receivingLog.driver),
          firstName: receivingLog.driver.first_name,
          eldMode: receivingLog.driver.eld_mode,
        },
      },
      isAssignable: !!drivingPeriod.isAssignable,
      isEditable: !!data.editable,
      restrictionReason: drivingPeriod.restrictionReason,
      showReassignButton: drivingPeriod.showReassignButton,
      navigation: drivingPeriod.navigation || data._navigation,
      drivingPeriodNav: drivingPeriod.drivingPeriodNav || data.driving_period_nav,
      isClaimed: data.is_claimed,
      isUnregulated: data.regulation_mode === VEHICLE_REGULATION_MODES.UNREGULATED,
      suggestedDriver: (data.suggested_driver && data.suggested_driver.id) || (data.suggested_driver?.driver_facial_profile_id
        && this.complianceFeatureFlagService.isComplianceTripsNewAILabelsPhaseTwoEnabled()) ? {
          id: data.suggested_driver.id,
          name: fullName(data.suggested_driver),
          firstName: data.suggested_driver.first_name,
          imageUrl: data.suggested_driver.bounded_image_url,
          imageDate: moment(data.suggested_driver.image_datetime),
          boundedImageUrl: data.suggested_driver.bounded_image_url,
          suggestedBy: DRIVER_SUGGESTED_BY_MAPPING[data.suggested_driver.suggested_by],
          profileId: data.suggested_driver.driver_facial_profile_id,
          aiSuggestionParams,
          isAssignable: isSuggestedDriverAssignable,
        } : null,
      unassignable: drivingPeriod.unassignable,
      driverFacingImages,
      hideDriverSuggestion: data.hideDriverSuggestion,
      prevTrip: data.prev_period && (data.prev_period.driver || data.prev_period.end_time) ? {
        driver: data.prev_period.driver?.id ? {
          id: data.prev_period.driver.id,
          name: fullName({ first_name: data.prev_period.driver.first_name, last_name: data.prev_period.driver.last_name }),
          isAssignable: !!drivingPeriod.isAssignable && isPreviousDriverAssignable,
        } : null,
        endTime: data.prev_period.end_time ? data.prev_period.end_time : null,
      } : null,
      nextTrip: data.next_period && (data.next_period.driver || data.next_period.start_time) ? {
        driver: data.next_period.driver?.id ? {
          id: data.next_period.driver.id,
          name: fullName({ first_name: data.next_period.driver.first_name, last_name: data.next_period.driver.last_name }),
          isAssignable: !!drivingPeriod.isAssignable && isNextDriverAssignable,
        } : null,
        startTime: data.next_period && data.next_period.start_time ? data.next_period.start_time : null,
      } : null,
    };
  }

  private changedAttributes(attrs: IServerChangedAttribute): IChangedAttribute {
    if (attrs) {
      const objToReturn: IChangedAttribute = {};
      if (attrs.start_address !== undefined) objToReturn.startAddress = attrs.start_address;
      if (attrs.start_description !== undefined) objToReturn.startDescription = attrs.start_description;
      if (attrs.end_address !== undefined) objToReturn.endAddress = attrs.end_address;
      if (attrs.end_description !== undefined) objToReturn.endDescription = attrs.end_description;
      if (attrs.notes !== undefined) objToReturn.notes = attrs.notes;
      if (attrs.notes_added_by) {
        objToReturn.notesAuthor = fullName(attrs.notes_added_by);
        objToReturn.notesAuthorId = attrs.notes_added_by.id;
      }
      if (attrs.special_duty_status) objToReturn.special_duty_status = mapSdsEventType(attrs.special_duty_status);
      return objToReturn;
    } else return null;
  }

  private getLocations(): IDpLocation[] {
    const bearingCheck = (bearing: number) => bearing === null ? undefined : bearing;
    const locations = this.data.locations;
    return locations?.map(l => {
      const location = l.location;
      return {
        lat: location.lat,
        lng: location.lon,
        bearing: bearingCheck(location.bearing),
      };
    });
  }

  private getDriverAssignableStatus(data: any): boolean {
    if (!data) return false;
    const driver = data.driver ? data.driver : data;

    if (driver) {
      const assignableStatus = driver.trip_assignable_status;
      const isAssignable = assignableStatus === 'ok' ||
        (driver.eld_mode === ELD_MODES.EXEMPTED &&
          mapRestrictionReason(assignableStatus) === RESTRICTION_REASON.NEXT_DAY_LOG_NOT_PRESENT
        );

      return isAssignable;
    }
    return false;
  }

}
